import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { sanitizeReleaseNote } from '@sv-support-sdk/utils/src/helpers/sanitizeContent';
import useMounted from '@sv-support-sdk/utils/src/hooks/useMounded';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';

import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Collapse from '../../components/Navigation/Collapse';
import Article from '../../components/Content/Article';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  isReleaseNotesLoadedSelector,
  nextRequestParamsSelector,
  selectLoading,
  selectReleaseNotes,
} from '../../store/selectors/releaseNotes';
import { $l } from '../../locales';
import useMobileDetect from '../../hooks/useMobileDetect';
import LoaderFallback from '../../components/Feedback/Loader/LoaderFallback';
import releaseNotesActions from '../../store/actions/releaseNotes';
import type { OutletContextType } from '../../types/outlet';

const ReleaseNotes = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMobileDetect();
  const releaseNotes = useAppSelector(selectReleaseNotes);
  const loading = useAppSelector(selectLoading);
  const isReleaseNotesLoaded = useAppSelector(isReleaseNotesLoadedSelector);
  const nextRequestParams = useAppSelector(nextRequestParamsSelector);
  const isMounted = useMounted();
  const {
    scrollToBottom: { isBottom },
  } = useOutletContext<OutletContextType>();

  useEffect(() => {
    if (nextRequestParams.page === 1) {
      dispatch(releaseNotesActions.getReleaseNotes(nextRequestParams));
    }
  }, []);

  useEffect(() => {
    if (isBottom && !isReleaseNotesLoaded && isMounted) {
      dispatch(releaseNotesActions.getReleaseNotes(nextRequestParams));
    }
  }, [isBottom, isReleaseNotesLoaded, isMounted]);

  if (loading && !releaseNotes.length) {
    return (
      <PageContentInner height="full">
        <LoaderFallback />
      </PageContentInner>
    );
  }

  return (
    <PageContentInner>
      <Headline className="indent-xl-bottom">{$l('RELEASE_NOTES.TITLE')}</Headline>
      <List gap={isMobile ? undefined : '16'} indents="0-0-24">
        {releaseNotes &&
          releaseNotes.map((releaseNote, index) => (
            <Collapse
              key={releaseNote.slug}
              icon="32--check-circle-outlined"
              title={releaseNote.header}
              subtitle={releaseNote.date}
              arrowType="text"
              expanded={index === 0}
              isMobile={isMobile}
            >
              <Article size="md" className="indent-md-top">
                <div
                  dangerouslySetInnerHTML={sanitizeReleaseNote(releaseNote.content, {
                    noteTitle: $l('HELP_CENTER_TOPIC.NOTE'),
                    tipTitle: $l('HELP_CENTER_TOPIC.TIP'),
                  })}
                ></div>
              </Article>
            </Collapse>
          ))}
      </List>
      {loading && <LoaderFallback className="indent-md-top" />}
    </PageContentInner>
  );
};

export default ReleaseNotes;
